import React, { InputHTMLAttributes, useState } from 'react';

export enum Sizes {
  xs = 'xs',
  sm = 'sm',
  base = 'base',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  '2xl' = '2xl',
  '3xl' = '3xl',
  '4xl' = '4xl',
  '5xl' = '5xl'
}

interface NumberSelectorProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  value?: number;
  unit?: string;
  unitPosition?: 'prefix' | 'suffix';
  variant?: Sizes.sm | Sizes.md | Sizes.lg;
  values?: number[]; // Nouvelle propriété pour la liste de valeurs
  onChange?: (value: number) => void;
}

export const NumberSelector: React.FC<NumberSelectorProps> = ({
  min = 0,
  max = 100,
  step = 1,
  defaultValue,
  value,
  unit,
  unitPosition = 'suffix',
  variant = Sizes.md,
  values, // Nouvelle propriété
  onChange,
  className,
  disabled,
  id = '',
  ...inputProps
}) => {
  // Handle both controlled and uncontrolled component patterns
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() => {
    if (isControlled) return value;
    if (defaultValue !== undefined) return defaultValue;
    return values ? values[0] : min;
  });

  // État séparé pour la valeur en cours de saisie (peut dépasser les limites)
  const [inputValue, setInputValue] = useState<string>(() => {
    if (isControlled) return value.toString();
    if (defaultValue !== undefined) return defaultValue.toString();
    return values ? values[0].toString() : min.toString();
  });

  // Sync with controlled value when it changes
  React.useEffect(() => {
    if (isControlled) {
      setInternalValue(value);
      setInputValue(value.toString());
    }
  }, [isControlled, value]);

  // Current value to display
  const currentValue = isControlled ? value : internalValue;

  // Configuration des tailles
  const sizeConfig = {
    [Sizes.sm]: {
      container: 'max-w-[10rem]',
      button: 'p-2',
      input: 'h-9 text-sm py-2',
      icon: 'w-1.5 h-1.5',
      unitOffset: {
        prefix: 'left-10',
        suffix: 'text-sm right-2.5 -mr-0.5'
      }
    },
    [Sizes.md]: {
      container: 'max-w-[12rem]',
      button: 'p-3',
      input: 'h-11 text-sm py-2.5',
      icon: 'w-2 h-2',
      unitOffset: {
        prefix: 'left-12',
        suffix: 'right-3 -mr-1.5'
      }
    },
    [Sizes.lg]: {
      container: 'max-w-[14rem]',
      button: 'p-4',
      input: 'h-12 text-base py-3',
      icon: 'w-2.5 h-2.5',
      unitOffset: {
        prefix: 'left-14',
        suffix: 'right-4 -mr-2'
      }
    }
  };

  const currentSizeConfig = sizeConfig[variant];

  // Fonctions pour la navigation dans les valeurs prédéfinies
  const getNextValueFromList = (currentVal: number): number => {
    if (!values || values.length === 0) return currentVal;

    const currentIndex = values.indexOf(currentVal);
    if (currentIndex === -1) {
      // Si la valeur actuelle n'est pas dans la liste, prendre la première valeur
      return values[0];
    }

    // Passer à la valeur suivante, ou rester à la dernière si on est au bout
    return currentIndex < values.length - 1 ? values[currentIndex + 1] : values[currentIndex];
  };

  const getPreviousValueFromList = (currentVal: number): number => {
    if (!values || values.length === 0) return currentVal;

    const currentIndex = values.indexOf(currentVal);
    if (currentIndex === -1) {
      // Si la valeur actuelle n'est pas dans la liste, prendre la première valeur
      return values[0];
    }

    // Passer à la valeur précédente, ou rester à la première si on est au début
    return currentIndex > 0 ? values[currentIndex - 1] : values[currentIndex];
  };

  // Handle increment/decrement/input change logic
  const handleIncrement = () => {
    if (disabled) return;

    let newValue: number;

    if (values && values.length > 0) {
      // Mode liste de valeurs : passer à la valeur suivante
      newValue = getNextValueFromList(currentValue);
    } else {
      // Mode classique : incrémenter avec min/max
      newValue = Math.min(currentValue + step, max);
    }

    setInternalValue(newValue);
    setInputValue(newValue.toString());
    if (onChange) onChange(newValue);
  };

  const handleDecrement = () => {
    if (disabled) return;

    let newValue: number;

    if (values && values.length > 0) {
      // Mode liste de valeurs : passer à la valeur précédente
      newValue = getPreviousValueFromList(currentValue);
    } else {
      // Mode classique : décrémenter avec min/max
      newValue = Math.max(currentValue - step, min);
    }

    setInternalValue(newValue);
    setInputValue(newValue.toString());
    if (onChange) onChange(newValue);
  };

  // Permet la saisie de n'importe quelle valeur numérique sans contrainte immédiate
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const newInput = e.target.value;

    // Si l'entrée est vide ou juste un signe négatif, permettre la saisie
    if (newInput === '' || newInput === '-') {
      setInputValue(newInput);
      return;
    }

    // Vérifier si c'est un nombre valide
    const newValue = parseFloat(newInput);
    if (!isNaN(newValue)) {
      setInputValue(newInput);
    }
  };

  // Fonction pour ajuster la valeur après que l'utilisateur ait terminé la saisie
  const handleFinalizeValue = () => {
    if (disabled) return;

    let finalValue: number;

    // Si l'entrée est vide, utiliser la valeur par défaut appropriée
    if (inputValue === '' || inputValue === '-') {
      finalValue = values && values.length > 0 ? values[0] : min;
    } else {
      let newValue = parseFloat(inputValue);
      if (isNaN(newValue)) {
        finalValue = values && values.length > 0 ? values[0] : min;
      } else {
        if (values && values.length > 0) {
          // Mode liste de valeurs : trouver la valeur la plus proche dans la liste
          finalValue = values.reduce((closest, current) => {
            return Math.abs(current - newValue) < Math.abs(closest - newValue) ? current : closest;
          });
        } else {
          // Mode classique : ajustement aux bornes
          finalValue = Math.max(min, Math.min(max, newValue));
        }
      }
    }

    // Mettre à jour les deux états avec la valeur finale
    setInputValue(finalValue.toString());
    setInternalValue(finalValue);

    if (onChange) onChange(finalValue);
  };

  // Déterminer les conditions de désactivation des boutons
  const isDecrementDisabled = () => {
    if (disabled) return true;

    if (values && values.length > 0) {
      const currentIndex = values.indexOf(currentValue);
      return currentIndex <= 0 || currentIndex === -1;
    }

    return currentValue <= min;
  };

  const isIncrementDisabled = () => {
    if (disabled) return true;

    if (values && values.length > 0) {
      const currentIndex = values.indexOf(currentValue);
      return currentIndex >= values.length - 1 || currentIndex === -1;
    }

    return currentValue >= max;
  };

  return (
    <div className={`relative flex items-center ${className || currentSizeConfig.container}`}>
      {unit && unitPosition === 'prefix' && (
        <span className={`absolute ${currentSizeConfig.unitOffset.prefix} z-10 text-gray-500 pointer-events-none`}>
          {unit}
        </span>
      )}

      <button
        type="button"
        onClick={handleDecrement}
        disabled={isDecrementDisabled()}
        className={`bg-gray-100 hover:bg-gray-200 rounded-full ${currentSizeConfig.button} focus:ring-gray-100 focus:ring-2 focus:outline-none ${isDecrementDisabled() ? 'opacity-50 cursor-auto' : ''
          }`}
        aria-label="Decrease value"
      >
        <svg
          className={`${currentSizeConfig.icon} text-gray-900`}
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 18 2"
        >
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1h16" />
        </svg>
      </button>

      <div className="relative">
        <input
          type="text"
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleFinalizeValue}
          onKeyDown={(e) => e.key === 'Enter' && handleFinalizeValue()}
          disabled={disabled}
          className={`${currentSizeConfig.input} text-center text-gray-900 focus:ring-primary block w-full 
            ${unit && unitPosition === 'prefix' ? 'pl-3' : ''} 
            ${unit && unitPosition === 'suffix' ? 'pr-3' : ''}`}
          min={values ? undefined : min}
          max={values ? undefined : max}
          step={values ? undefined : step}
          {...inputProps}
        />

        {unit && unitPosition === 'suffix' && (
          <span className={`absolute ${currentSizeConfig.unitOffset.suffix} top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none`}>
            {unit}
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={handleIncrement}
        disabled={isIncrementDisabled()}
        className={`bg-gray-100 hover:bg-gray-200 rounded-full ${currentSizeConfig.button} focus:ring-gray-100 focus:ring-2 focus:outline-none ${isIncrementDisabled() ? 'opacity-50 cursor-auto' : ''
          }`}
        aria-label="Increase value"
      >
        <svg
          className={`${currentSizeConfig.icon} text-gray-900`}
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 18 18"
        >
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 1v16M1 9h16" />
        </svg>
      </button>
    </div>
  );
};
